/**
 * The guest's own half of the marketplace, spoken directly rather than through the member client.
 *
 * <p>A visitor here has no Scheda account and no member token, so nothing in `api/client` applies:
 * their credentials are a cookie for their own session and, per host, a short-lived bearer minted
 * from it. Routing that through the member client would mean teaching it about a second kind of
 * caller for no gain, and the one thing it does automatically — attaching the member's token — is
 * the one thing that must not happen here.</p>
 */
const BASE = import.meta.env.VITE_API_BASE ?? "";

/** What the page is presenting on this visitor's behalf. Never anything about any host (D-43). */
export interface GuestSession {
  signed_in: boolean;
  given_name: string | null;
  identities: GuestIdentity[];
  /** What a token's audience is built from. Served, never guessed from the page's own origin. */
  audience_base: string;
  /** What they would hand over, and whether they have. Null before anything is proved. */
  sharing: GuestSharing | null;
}

/**
 * What a visitor agrees to leave behind (D-92).
 *
 * <p>`consented` is the presence of the stored profile and nothing else, so there is no state where
 * they have declined and it is kept anyway.</p>
 */
export interface GuestSharing {
  consented: boolean;
  /** Null when the identity they proved carries no address, so there is nothing to agree to. */
  proposed: { name: string | null; email: string; email_verified: boolean } | null;
}

export interface GuestIdentity {
  provider: string;
  email: string | null;
  email_verified: boolean;
}

/**
 * The single refusal, as it reaches the page.
 *
 * <p>It carries a code and a fixed message and nothing else, and that is the whole design: every
 * negative cause produces this exact object, so there is nothing here to branch on and nothing to
 * report beyond what it says. Code paths that try to be helpful about *why* are the leak (D-8).</p>
 */
export class MarketplaceRefused extends Error {
  constructor(public readonly code: number, message: string) {
    super(message);
  }
}

export async function getGuestSession(): Promise<GuestSession> {
  const res = await fetch(`${BASE}/api/v1/marketplace/session`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`guest session: ${res.status}`);
  return (await res.json()) as GuestSession;
}

export async function endGuestSession(): Promise<void> {
  await fetch(`${BASE}/api/v1/marketplace/session`, {
    method: "DELETE",
    credentials: "include",
  });
}

/**
 * Agree to hand over a name and an address.
 *
 * <p>Asked once, up front, before anything has gone anywhere. Never at the moment a request reaches
 * a host: a question that only appears once somebody has been admitted answers itself (D-44, D-80).</p>
 */
export async function shareGuestProfile(): Promise<GuestSharing> {
  const res = await fetch(`${BASE}/api/v1/marketplace/session/sharing`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`share: ${res.status}`);
  return (await res.json()) as GuestSharing;
}

/** Take it back. A delete, because presence is the consent. */
export async function stopSharingGuestProfile(): Promise<GuestSharing> {
  const res = await fetch(`${BASE}/api/v1/marketplace/session/sharing`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`stop sharing: ${res.status}`);
  return (await res.json()) as GuestSharing;
}

/** A bearer for exactly one host, minted from every identity on the session (D-42). */
export async function mintGuestToken(audienceBase: string, handle: string): Promise<string> {
  const res = await fetch(`${BASE}/api/v1/marketplace/session/token`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ audience: `${audienceBase}/agents/${strip(handle)}` }),
  });
  if (res.status === 401) throw new MarketplaceRefused(401, "Your session has ended.");
  if (!res.ok) throw new MarketplaceRefused(res.status, "That could not be asked.");
  const body = (await res.json()) as { access_token: string };
  return body.access_token;
}

let nextId = 1;

/**
 * One JSON-RPC call to a host's gateway.
 *
 * <p>Throws {@link MarketplaceRefused} on any error member. There is deliberately no branching on
 * the code beyond passing it through: the gateway answers every negative cause with one identical
 * object, so a client that treated some refusals differently from others would be inventing a
 * distinction the protocol went to some trouble not to make.</p>
 */
export async function marketplaceRpc<T>(
  token: string,
  method: string,
  params: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`${BASE}/api/v1/marketplace/rpc`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ jsonrpc: "2.0", id: `web-${nextId++}`, method, params }),
  });

  const body = (await res.json().catch(() => null)) as
    | { result?: T; error?: { code: number; message: string } }
    | null;

  if (!body) throw new MarketplaceRefused(-32603, "Something went wrong.");
  if (body.error) throw new MarketplaceRefused(body.error.code, body.error.message);
  return body.result as T;
}

export interface AvailabilityResult {
  session_id: string;
  expires_at: string;
  duration_minutes: number;
  time_zone: string;
  spans: { start: string; end: string }[];
}

/** What a booked meeting is, once the page has one to move. */
export interface Booked {
  sessionId: string;
  token: string;
  handle: string;
  start: string;
}

export interface ConfirmResult {
  session_id: string;
  status: "CONFIRMED" | "PENDING_HOST";
  booking_id?: string;
  start: string;
  end: string;
  hold_expires_at?: string;
  manage_url?: string;
}

/**
 * Whether this browser holds a guest session, answered without a network call.
 *
 * <p>The session cookie is HttpOnly and unreadable here on purpose. Its companion carries no value
 * and exists only so a cold load can tell an inert first paint from a working one, because before
 * an identity is proved the route is the bundle and nothing else (D-37, D-77).</p>
 */
export function hasGuestSession(cookies: string = readCookies()): boolean {
  return cookies
    .split(";")
    .map((c) => c.trim())
    .some((c) => c.startsWith("scheda_guest_present="));
}

/** No document means no cookie, which means the cold, inert view. Never a crash. */
function readCookies(): string {
  return typeof document === "undefined" ? "" : document.cookie;
}

function strip(handle: string): string {
  const trimmed = handle.trim();
  return (trimmed.startsWith("@") ? trimmed.slice(1) : trimmed).toLowerCase();
}

/**
 * <b>One line, for every reason.</b> Not a switch on the error code with a nicer message per case:
 * the gateway answers every negative cause with the same object precisely so this cannot be
 * written, and a page that inferred more than the wire said would rebuild by hand the oracle the
 * protocol spent a timing floor and a fixed message refusing (D-8, D-44).
 *
 * <p>The one exception is the visitor's own session lapsing. That is a fact about them rather than
 * about anybody else, and it is theirs to know.</p>
 */
export function refusalLine(error: unknown, onSignedOut?: () => void): string {
  if (error instanceof MarketplaceRefused && error.code === 401) {
    onSignedOut?.();
    return "Your session ended. Prove who you are again to keep going.";
  }
  return NOTHING_ON_OFFER;
}

export const NOTHING_ON_OFFER = "No availability.";

/** The same refusal, worded for somebody who is not asking for a first meeting but a different one. */
export const NOTHING_ELSE_ON_OFFER = "No availability. Your current time still stands.";

/** Said after a refused move, because what happened to their existing meeting is theirs to know. */
export const STILL_STANDS = "Your current time still stands.";
